import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import DropdownMenu from '../components/DropdownMenu';
import { profileIdentity } from '../lib/profileService';
import './AppHeader.css';

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  width: 18,
  height: 18,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function DashboardIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}

function initials(name) {
  return (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function Avatar({ name, imageUrl }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="AppHeader-avatar">
      {imageUrl && !broken ? (
        <img src={imageUrl} alt="" onError={() => setBroken(true)} />
      ) : (
        <span>{initials(name)}</span>
      )}
    </span>
  );
}

// Menu général de l'application : marque à gauche, navigation au centre, compte à droite.
function AppHeader({ profile, email, crumb, guardedNavigate, onSignOut }) {
  const navigate = useNavigate();
  const identity = profileIdentity(profile, email);

  // Les liens passent par le garde de la page (modifications non enregistrées) avant de naviguer.
  const onNavClick = (to) => (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    guardedNavigate(() => navigate(to));
  };

  return (
    <header className="AppHeader">
      <a className="AppHeader-brand" href="/" onClick={onNavClick('/')} aria-label="MyTravo, tableau de bord">
        My<span>Travo</span>
      </a>

      <nav className="AppHeader-nav" aria-label="Navigation principale">
        <NavLink to="/" end className="AppHeader-link" onClick={onNavClick('/')}>
          <DashboardIcon />
          <span className="AppHeader-linkText">Tableau de bord</span>
        </NavLink>
        {crumb && (
          <>
            <span className="AppHeader-crumbSep" aria-hidden="true">
              ›
            </span>
            <span className="AppHeader-crumb" aria-current="page" title={crumb}>
              {crumb}
            </span>
          </>
        )}
      </nav>

      <div className="AppHeader-account">
        <DropdownMenu
          align="right"
          ariaLabel={`Compte : ${identity.displayName}`}
          buttonClassName="AppHeader-userBtn"
          label={
            <>
              <span className="AppHeader-userText">
                <span className="AppHeader-userName">{identity.displayName}</span>
                {identity.roleLabel && <span className="AppHeader-userRole">{identity.roleLabel}</span>}
              </span>
              <Avatar name={identity.displayName} imageUrl={identity.imageUrl} />
            </>
          }
          header={
            <div className="AppHeader-menuHeader">
              <span className="AppHeader-menuName">{identity.displayName}</span>
              {identity.email && <span className="AppHeader-menuEmail">{identity.email}</span>}
            </div>
          }
          items={[
            {
              key: 'guide',
              label: "Guide de l'app mobile",
              icon: (
                <svg {...ICON_PROPS}>
                  <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
                  <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
                </svg>
              ),
              onSelect: () => guardedNavigate(() => navigate('/guide')),
            },
            {
              key: 'logout',
              label: 'Déconnexion',
              danger: true,
              icon: (
                <svg {...ICON_PROPS}>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              ),
              onSelect: () => guardedNavigate(onSignOut),
            },
          ]}
        />
      </div>
    </header>
  );
}

export default AppHeader;
